import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Form } from 'antd';
import { useLocation } from 'react-router-dom';

import { FSCollections } from '@src/constants/database';
import useCloudFunction from '@src/packages/utils/hooks/clouds/useCloudFunction';
import {
  DEFAULT_SHIFT_TYPE_OPTIONS,
  SHIFT_TYPES,
  SHIFT_TYPES_OPTIONS,
} from '@src/packages/shifts/constants';
import { useIsMobile, useIsSelfModeTimeTracker } from '@src/packages/utils';
import { useRulesByCountry } from '@src/packages/utils/hooks/useRulesByCountry';
import { API_OPERATIONS } from '@src/util/cloud-functions/operations';

import { PERIOD_OPTIONS } from '@src/containers/App/TimeTracker/Main/components/constants';
import { DT_PROCESS_TYPES, PATHS } from '../../../constants';
import { useTimeTracker } from '../../../contexts/TimeTrackerProvider';
import { ALERT_TYPES_FIELDS } from '../AlertFiltersModal';
import { ReportContext } from '../../contexts/ReportContext';
import TimeTrackerUtils from '../../../utils';
import { hasActiveGroupFilters } from '../../../utils/usersSelectedParams';

const LIMIT_USERS = 50;
const GROU_USERS_PER_PAGE = 20;

export const formatUserSelectLabel = (user, countryRulesForm) => {
  if (!user) return '';

  const idCard = user.idCard
    ? `(${countryRulesForm?.identifier?.formatIdentifier(user.idCard)})`
    : '';

  return `${user.firstName ?? ''} ${user.lastName ?? ''} ${idCard}`.trim();
};

const WORK_JOURNEYS_THAT_CLEAR_SHIFT = [
  SHIFT_TYPES.FIXED,
  SHIFT_TYPES.CYCLE,
  SHIFT_TYPES.BIWEEKLY,
  SHIFT_TYPES.PART_TIME,
];

const useTimeTrackerFilters = ({ form, onSubmit, refresh, onHashSubmit }) => {
  const location = useLocation();
  const { isMobile } = useIsMobile();
  const filterRef = useRef(null);

  const [alertType, setAlertType] = useState(null);
  const [totalUsersPerOrg, setTotalUsersPerOrg] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [refreshGroupUsers, setRefreshGroupUsers] = useState(false);
  const [groupFilters, setGroupFilters] = useState({
    workJourneys: null,
    shiftId: null,
    regionIds: null,
    addressIds: null,
    roleIds: null,
    companyId: null,
  });

  const { organizationIds, loading, country } = useTimeTracker();
  const { setUsersToSelect, setSelectedIndividualUser } = useContext(ReportContext);
  const { supervisorThirdParty, externalUserInfo, isSelfMode } = useIsSelfModeTimeTracker();

  const userIdStorage = localStorage.getItem('user_id');

  const { countryRulesForm } = useRulesByCountry({ countryName: country });
  const path = location?.pathname;
  const reportType = path.split('/').pop();

  const workJourneysValue = Form.useWatch('workJourneys', form) || [];
  const periodValue = Form.useWatch('period', form);
  const startDateValue = Form.useWatch('startDate', form);
  const endDateValue = Form.useWatch('endDate', form);
  const regionValue = Form.useWatch('region', form);
  const userIdValue = Form.useWatch('userId', form);
  const hashValue = Form.useWatch('hash', form);

  const toApiStringIdArray = value =>
    value != null && Array.isArray(value) && value.length > 0 ? value : null;

  const buildGroupUsersFilters = () => {
    const hasWorkJourneysFilter = groupFilters.workJourneys != null;
    const hasExceptional = groupFilters.workJourneys?.includes(SHIFT_TYPES_OPTIONS.EXCEPTIONAL);

    const exceptionalShift = hasWorkJourneysFilter ? hasExceptional : null;

    const workShiftType = hasExceptional
      ? []
      : groupFilters.workJourneys?.filter(wj => wj !== SHIFT_TYPES_OPTIONS.EXCEPTIONAL) || [];

    return {
      workShiftType,
      shiftId: groupFilters.shiftId || null,
      regionIds: toApiStringIdArray(groupFilters.regionIds),
      addressIds: toApiStringIdArray(groupFilters.addressIds),
      roleIds: toApiStringIdArray(groupFilters.roleIds),
      companyId: groupFilters.companyId || null,
      exceptionalShift,
    };
  };

  const groupUsersParamsMemo = useMemo(() => {
    const filters = buildGroupUsersFilters();
    return {
      organizationIds,
      ...filters,
      // Numbered pages: page N is an offset, so any page (or going back) can be requested directly.
      from: (currentPage - 1) * GROU_USERS_PER_PAGE,
      sort: [{ field: 'lastName.keyword', order: 'asc' }],
    };
  }, [JSON.stringify(organizationIds), JSON.stringify(groupFilters), currentPage]);

  const buildWorkShiftFilters = workJourneys => {
    const resolvedWorkJourneys =
      workJourneys?.length > 0 ? workJourneys : [SHIFT_TYPES_OPTIONS.SHIFT_WORK];

    const hasExceptional = resolvedWorkJourneys.includes(SHIFT_TYPES_OPTIONS.EXCEPTIONAL);
    const hasShiftWork = resolvedWorkJourneys.includes(SHIFT_TYPES_OPTIONS.SHIFT_WORK);

    if (hasShiftWork) {
      return { terms: { type: [SHIFT_TYPES_OPTIONS.SHIFT_WORK] } };
    }

    const hasClearShiftTypes = resolvedWorkJourneys.some(wj =>
      WORK_JOURNEYS_THAT_CLEAR_SHIFT.includes(wj),
    );

    const hasDatesSelected =
      (periodValue != null && periodValue !== '') ||
      (startDateValue != null && startDateValue !== '') ||
      (endDateValue != null && endDateValue !== '');

    const policyClause = {
      nested: { path: 'policies', query: { exists: ['policies.id'] } },
    };

    const shiftClause = {
      terms: { type: [SHIFT_TYPES_OPTIONS.SHIFT_WORK] },
    };

    const shouldUsePolicySearch = !hasClearShiftTypes && (hasExceptional || hasDatesSelected);

    if (shouldUsePolicySearch) {
      const should =
        hasDatesSelected && !hasExceptional
          ? [shiftClause, policyClause]
          : hasShiftWork || hasDatesSelected
            ? [policyClause, shiftClause]
            : [policyClause];

      return {
        should,
        minimumShouldMatch: 1,
      };
    }

    if (hasClearShiftTypes) {
      return { terms: { type: [] } };
    }

    return { terms: { type: resolvedWorkJourneys } };
  };

  const workShiftsParamsMemo = useMemo(
    () => ({
      entity: 'work_shift',
      divisionIds: [],
      organizationId: organizationIds[0],
      pagination: { from: 0, size: 100 },
      filters: buildWorkShiftFilters(workJourneysValue),
    }),
    [
      JSON.stringify(organizationIds),
      JSON.stringify(workJourneysValue),
      periodValue,
      startDateValue,
      endDateValue,
    ],
  );

  /** Group Users */
  const { data: groupUsers, loading: groupUsersLoading } = useCloudFunction({
    op: API_OPERATIONS.FIND_USERS_DT,
    params: groupUsersParamsMemo,
    parseData: data => {
      return {
        users:
          data?.hits?.hits?.map(item => {
            const employeeData = item?._source;
            const contractor = employeeData?.subcontractorName
              ? `${employeeData?.subcontractorName} - ${countryRulesForm?.identifier?.formatIdentifier(employeeData?.subcontractorIdentifier)}`
              : null;

            const user = {
              id: employeeData?.id || item?._id,
              name: employeeData?.employeeFullname,
              rut: countryRulesForm?.identifier?.formatIdentifier(employeeData?.employeeIdentifier),
              workplace: TimeTrackerUtils.getAddressString(employeeData?.workJourneyAddress),
              job: employeeData?.employeeJobName || '-',
              contractor,
            };

            return user;
          }) || [],
        total: data?.aggregations?.totalEmployees?.value,
      };
    },
    dependencies: [groupUsersParamsMemo, refreshGroupUsers],
  });

  /** Work Shifts */
  const { data: workShifts, loading: workShiftsLoading } = useCloudFunction({
    op: API_OPERATIONS.QUERY,
    params: workShiftsParamsMemo,
    parseData: data => {
      const hasExceptional = workJourneysValue.includes(SHIFT_TYPES_OPTIONS.EXCEPTIONAL);
      const hasShiftWork = workJourneysValue.includes(SHIFT_TYPES_OPTIONS.SHIFT_WORK);
      const shouldIncludePolicyShifts = hasExceptional && !hasShiftWork;
      const hits = data?.hits?.hits ?? [];
      const filteredHits = hits.filter(item => {
        const source = item?._source;
        const hasPolicies = (source?.policies?.length ?? 0) > 0;

        if (!hasPolicies) return true;
        if (!shouldIncludePolicyShifts) return false;

        return source?.type === SHIFT_TYPES_OPTIONS.SHIFT_WORK;
      });

      return filteredHits.map(item => ({
        id: item._id,
        label: item?._source?.regulationName || item?._source?.name,
        value: item?._id,
      }));
    },
    runFirstTime: organizationIds?.length > 0 ? true : false,
    dependencies: [workShiftsParamsMemo, organizationIds?.length],
  });

  /** User Jobs */
  const { data: userJobs, loading: userJobsLoading } = useCloudFunction({
    op: API_OPERATIONS.GENERIC_FIND,
    params: {
      organizationIds: organizationIds,
      filters: [],
      entity: FSCollections.USER_JOBS,
    },
    parseData: data => {
      return data?.hits?.hits?.map(item => ({
        id: item._id,
        label: item._source.name,
        value: item._id,
      }));
    },
    dependencies: [organizationIds.length],
  });

  /** Companies */
  const { data: companies, loading: companiesLoading } = useCloudFunction({
    op: API_OPERATIONS.GENERIC_FIND,
    params: {
      organizationIds: organizationIds,
      filters: [],
      entity: FSCollections.COMPANIES,
    },
    parseData: data => {
      return data?.hits?.hits?.map(item => ({
        id: item._source.id,
        label:
          item._source.name +
          ' - ' +
          countryRulesForm.identifier.formatIdentifier(item?._source?.identifier),
        value: item._source.id,
      }));
    },
    dependencies: [organizationIds.length],
  });

  /** Regions */
  const { data: regions, loading: regionsLoading } = useCloudFunction({
    op: API_OPERATIONS.GENERIC_FIND,
    params: {
      entity: FSCollections.ADDRESSES,
      organizationIds: organizationIds,
      filters: [],
    },
    parseData: data => {
      const seenRegions = new Set();
      const uniqueRegions = [];
      data?.hits?.hits?.forEach(item => {
        const region = item?._source?.region;
        if (region && !seenRegions.has(region)) {
          seenRegions.add(region);
          uniqueRegions.push({
            id: item._id,
            label: region,
            value: region,
          });
        }
      });
      return uniqueRegions;
    },
    dependencies: [organizationIds.length],
  });

  /** Premises */
  const premisesParamsMemo = useMemo(() => {
    const regions = Array.isArray(regionValue) ? regionValue : regionValue ? [regionValue] : [];
    const regionFilters = regions.filter(Boolean).map(r => ({ name: 'region.keyword', value: r }));
    return {
      entity: FSCollections.ADDRESSES,
      organizationIds: organizationIds,
      filters: regionFilters,
    };
  }, [organizationIds, regionValue]);

  const { data: premises, loading: premisesLoading } = useCloudFunction({
    op: API_OPERATIONS.GENERIC_FIND,
    params: premisesParamsMemo,
    parseData: data => {
      return data?.hits?.hits?.map(item => ({
        id: item._id,
        label: TimeTrackerUtils.getAddressString(item?._source),
        value: item?._id,
      }));
    },
    dependencies: [premisesParamsMemo],
  });

  const filtersData = useMemo(() => {
    const resolvedWorkJourneys =
      workJourneysValue?.length > 0 ? workJourneysValue : [SHIFT_TYPES_OPTIONS.SHIFT_WORK];

    const shouldShowShifts = resolvedWorkJourneys.includes(SHIFT_TYPES_OPTIONS.SHIFT_WORK);

    let filters = {
      workJourneys: DEFAULT_SHIFT_TYPE_OPTIONS.map(item => ({
        label: item.label,
        value: item.value,
      })),
      shifts: shouldShowShifts ? workShifts : [],
      regions,
      premises,
      jobs: userJobs,
      companies,
    };

    return filters;
  }, [
    JSON.stringify(workShifts),
    JSON.stringify(regions),
    JSON.stringify(premises),
    JSON.stringify(userJobs),
    JSON.stringify(companies),
    JSON.stringify(workJourneysValue),
  ]);

  const groupUserColumns = [
    {
      title: <span className="gx-font-weight-bold">Nombre</span>,
      dataIndex: 'name',
      key: 'name',
      width: 100,
      fixed: isMobile ? 'left' : undefined,
    },
    {
      title: <span className="gx-font-weight-bold">RUT</span>,
      dataIndex: 'rut',
      key: 'rut',
      width: 100,
    },
    {
      title: <span className="gx-font-weight-bold">Local, establecimiento o faena</span>,
      dataIndex: 'workplace',
      key: 'workplace',
      width: 100,
    },
    {
      title: <span className="gx-font-weight-bold">Cargo o funcion</span>,
      dataIndex: 'job',
      key: 'job',
      width: 100,
    },
    {
      title: <span className="gx-font-weight-bold">EST, contratista o subcontratista</span>,
      dataIndex: 'contractor',
      key: 'contractor',
      width: 100,
    },
  ];

  const periods = PERIOD_OPTIONS.filter(item => {
    if (reportType === PATHS.HOLIDAYS) {
      return true;
    }
    return item.value !== 365;
  });

  const onChangeUsers = usrs => {
    const hasSelection = Array.isArray(usrs) && usrs.length > 0;
    if (hasSelection) {
      setSelectedIndividualUser(null);
      form.setFieldsValue({ usersSelected: usrs, userId: '' });
      return;
    }
    form.setFieldsValue({ usersSelected: usrs });
  };

  const onChangePagination = page => {
    setCurrentPage(page);
  };

  const onSave = (next = false) => {
    const values = form.getFieldsValue();
    const { period, startDate, endDate } = values;

    if (next) {
      if (filterRef.current === DT_PROCESS_TYPES.HASH && onHashSubmit) {
        onHashSubmit();
        return;
      }

      let submitValues = values;
      if (filterRef.current === DT_PROCESS_TYPES.GROUP) {
        const selected = values.usersSelected;
        const hasManualSelection = Array.isArray(selected) && selected.length > 0;

        if (!hasManualSelection) {
          if (hasActiveGroupFilters(values)) {
            const groupUserIds = (groupUsers?.users ?? []).map(user => user.id);
            submitValues = { ...values, usersSelected: groupUserIds };
          } else {
            submitValues = { ...values, usersSelected: [] };
          }
        }
      }

      onSubmit({ values: submitValues, processType: filterRef.current });
      return;
    }

    setAlertType(null);

    if (filterRef.current === DT_PROCESS_TYPES.HASH) {
      onSave(true);
      return;
    }

    const hasSomeFilterByDate = period || startDate || endDate;

    if (!hasSomeFilterByDate) {
      setAlertType(ALERT_TYPES_FIELDS.MARGIN_RANGE_DATE);
      return;
    }

    if (totalUsersPerOrg > LIMIT_USERS) {
      setAlertType(ALERT_TYPES_FIELDS.LIMIT_USERS);
      return;
    }

    onSave(true);
  };

  const toGroupFilter = value => {
    if (value == null) return undefined;
    if (Array.isArray(value)) return value.length > 0 ? value : undefined;
    return value;
  };

  const filterGroupUsers = () => {
    const values = form.getFieldsValue();
    const workJourneys = values.workJourneys?.length > 0 ? values.workJourneys : undefined;
    setGroupFilters({
      workJourneys,
      shiftId: toGroupFilter(values.shift),
      regionIds: toGroupFilter(values.region),
      addressIds: toGroupFilter(values.premises),
      roleIds: toGroupFilter(values.jobs),
      companyId: toGroupFilter(values.companies),
    });
    setCurrentPage(1);
    setRefreshGroupUsers(prev => !prev);
  };

  const multiFormKeys = new Set(['workJourneys', 'shift', 'region', 'premises', 'jobs']);

  const formKeyToGroupFilterKey = {
    workJourneys: 'workJourneys',
    shift: 'shiftId',
    region: 'regionIds',
    premises: 'addressIds',
    jobs: 'roleIds',
    companies: 'companyId',
  };

  const onClearGroupFilters = key => {
    const stateKey = formKeyToGroupFilterKey[key] ?? key;
    setGroupFilters(prev => ({ ...prev, [stateKey]: undefined }));
    const empty = multiFormKeys.has(key) ? [] : undefined;
    form.setFieldsValue({ [key]: empty });
  };

  const QUERY_DEBOUNCE_SELECT = useMemo(() => {
    return {
      userId: {
        organizationIds: organizationIds,
        filters: [],
        entity: 'users',
        sort: [{ field: 'lastName.keyword', order: 'asc' }],
      },
    };
  }, [organizationIds]);

  const getSelectParsedData = data => {
    setUsersToSelect(data?.hits?.hits || []);
    setTotalUsersPerOrg(data?.hits?.total?.value ?? 0);
    return data?.hits?.hits
      .sort((a, b) => (a._source.lastName || '').localeCompare(b._source.lastName || ''))
      .map(item => ({
        label: formatUserSelectLabel(item._source, countryRulesForm),
        value: item._id,
      }));
  };

  const getUserSelectLabel = user => formatUserSelectLabel(user, countryRulesForm);

  const setProcessType = type => {
    filterRef.current = type;
  };

  useEffect(() => {
    setGroupFilters({});
    setCurrentPage(1);
    setRefreshGroupUsers(prev => !prev);
  }, [refresh]);

  useEffect(() => {
    if (isSelfMode) {
      form.setFieldsValue({ userId: userIdStorage });
    }
  }, [isSelfMode]);

  useEffect(() => {
    if (supervisorThirdParty) {
      form.setFieldsValue({ companies: externalUserInfo?.company?.id });
    }
  }, [supervisorThirdParty, externalUserInfo]);

  //Mount effect
  useEffect(() => {
    // set group filters from form
    setGroupFilters({
      workJourneys: form.getFieldsValue().workJourneys,
      shiftId: form.getFieldsValue().shift,
      regionIds: form.getFieldsValue().region,
      addressIds: form.getFieldsValue().premises,
      roleIds: form.getFieldsValue().jobs,
      companyId: form.getFieldsValue().companies,
    });
  }, []);

  return {
    // State
    alertType,
    setAlertType,
    currentPage,

    // Context data
    loading,
    isSelfMode,
    supervisorThirdParty,

    // Data
    groupUsers,
    groupUsersLoading,
    filtersData,
    periods,
    groupUserColumns,

    // Loading states
    workShiftsLoading,
    regionsLoading,
    premisesLoading,
    userJobsLoading,
    companiesLoading,

    // Form values
    userIdValue,
    hashValue,

    // Handlers
    onChangeUsers,
    onChangePagination,
    onSave,
    filterGroupUsers,
    setProcessType,
    onClearGroupFilters,

    // Debounce select
    QUERY_DEBOUNCE_SELECT,
    getSelectParsedData,
    getUserSelectLabel,

    // Constants
    GROU_USERS_PER_PAGE,
  };
};

export default useTimeTrackerFilters;
